import { Injectable } from "@angular/core";
import { StorageService } from "app/shared/services/storage.service";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IWorkorderOrServiceRequestSummary,
  IWorkorderRequestSummary,
  RequestSourceTypeEnum,
} from "../models/request";
import { CanUpdateActivityPipe } from "../pipes/can-update-activity";
import { isInternalWorkorderRequestPipe } from "../pipes/is-internal-workorder-request.pipe";
import { CurrentUserService } from "./current-user.service";
import { UtilService } from "./util.service";

export enum UpdateInBatchEnum {
  takeInCharge = "takeInCharge",
  cancelTakeInCharge = "cancelTakeInCharge",
  updateResponsible = "updateResponsible",
  updateActivity = "updateActivity",
  closeWorkorder = "closeWorkorder",
}

export interface IBatchMode {
  isActive: boolean;
  updateType?: UpdateInBatchEnum;
}

@Injectable({
  providedIn: "root",
})
export class RequestInBatchService {
  private readonly batchMode = new BehaviorSubject<IBatchMode>({
    isActive: false,
  });
  public batchMode$: Observable<IBatchMode>;
  private selectedRequestsInBatch: IWorkorderOrServiceRequestSummary[];
  private currentRequestActivity = "";
  private currentRequestGroupId: number;
  private currentRequestSource: RequestSourceTypeEnum;
  private canUpdateActivityPipe: CanUpdateActivityPipe;
  private isInternalWorkorderRequestPipe = new isInternalWorkorderRequestPipe();

  constructor(
    private utilService: UtilService,
    private storageService: StorageService,
    private currentUserService: CurrentUserService,
  ) {
    this.batchMode$ = this.batchMode.asObservable();
    this.selectedRequestsInBatch = [];
    this.canUpdateActivityPipe = new CanUpdateActivityPipe(currentUserService);
  }

  public activateBatchMode(updateType: UpdateInBatchEnum) {
    this.storageService.disabledTracking();
    this.resetSelectedRequests();
    this.batchMode.next({ isActive: true, updateType });
  }

  public desactivateBatchMode() {
    this.clearSelectedRequests();
    this.resetSelectedRequests();
    this.batchMode.next({ isActive: false });
  }

  private resetSelectedRequests() {
    const requests = this.storageService.getRequests();
    requests.forEach((request) => {
      request.isSelected = false;
    });
    this.storageService.setRequests(requests);
    this.utilService.clearSelectionFeature(
      this.storageService.getGeoJsonRequest(),
    );
  }

  public addSelectedRequest(request: IWorkorderOrServiceRequestSummary) {
    if (this.selectedRequestsInBatch.length === 0) {
      this.currentRequestActivity = request.service.code;
      this.currentRequestGroupId = request.responsible.groupId;
      this.currentRequestSource = request.source;
    }
    this.selectedRequestsInBatch.push(request);
  }

  public removeSelectedRequest(request: IWorkorderOrServiceRequestSummary) {
    const index = this.selectedRequestsInBatch.findIndex(
      (r) => r.id === request.id,
    );
    if (index > -1) {
      this.selectedRequestsInBatch.splice(index, 1);
    }

    if (this.selectedRequestsInBatch.length === 0) {
      this.currentRequestActivity = "";
      this.currentRequestGroupId = undefined;
      this.currentRequestSource = undefined;
    }
  }

  public clearSelectedRequests(): void {
    this.selectedRequestsInBatch = [];
    this.currentRequestActivity = "";
    this.currentRequestGroupId = undefined;
    this.currentRequestSource = undefined;
    this.utilService.clearSelectionFeature(
      this.storageService.getGeoJsonRequest(),
    );
  }

  public countSelectedRequestsInBatch(): number {
    return this.selectedRequestsInBatch.length;
  }

  public getSelectedRequestsInBatch(): IWorkorderOrServiceRequestSummary[] {
    return this.selectedRequestsInBatch;
  }

  public getCurrentRequestActivity(): string {
    return this.currentRequestActivity;
  }

  public getCurrentRequestGroupId(): number {
    return this.currentRequestGroupId;
  }

  public getCurrentRequestSource(): RequestSourceTypeEnum {
    return this.currentRequestSource;
  }

  public isRequestInBatchDisabled(
    request: IWorkorderOrServiceRequestSummary,
  ): boolean {
    const currentRequestGroupId = this.getCurrentRequestGroupId();
    const currentRequestActivity = this.getCurrentRequestActivity();
    const currentRequestSource = this.getCurrentRequestSource();

    switch (this.batchMode.value.updateType) {
      case UpdateInBatchEnum.takeInCharge: {
        const isWorkorderRequest =
          request.source === RequestSourceTypeEnum.workorderRequest;
        if (currentRequestActivity === "" && !currentRequestGroupId) {
          return isWorkorderRequest;
        }
        return (
          isWorkorderRequest ||
          currentRequestActivity !== request.service.code ||
          currentRequestGroupId !== request.responsible.groupId
        );
      }
      case UpdateInBatchEnum.updateResponsible: {
        if (
          currentRequestActivity === "" &&
          !currentRequestGroupId &&
          !currentRequestSource
        ) {
          return false;
        }
        return (
          currentRequestSource !== request.source ||
          currentRequestActivity !== request.service.code ||
          currentRequestGroupId !== request.responsible.groupId
        );
      }
      case UpdateInBatchEnum.updateActivity: {
        const canUpdateActivity = this.canUpdateActivityPipe.transform(request);
        if (
          currentRequestActivity === "" &&
          !currentRequestGroupId &&
          !currentRequestSource
        ) {
          return !canUpdateActivity;
        }
        return (
          !canUpdateActivity ||
          currentRequestSource !== request.source ||
          currentRequestActivity !== request.service.code ||
          currentRequestGroupId !== request.responsible.groupId
        );
      }
      case UpdateInBatchEnum.cancelTakeInCharge: {
        const isServiceRequest =
          request.source === RequestSourceTypeEnum.serviceRequest;
        let isInternalWorkorderRequest;
        if (!isServiceRequest) {
          isInternalWorkorderRequest =
            this.isInternalWorkorderRequestPipe.transform(request);
        }
        if (currentRequestActivity === "" && !currentRequestGroupId) {
          return isServiceRequest || isInternalWorkorderRequest;
        }
        return (
          isServiceRequest ||
          isInternalWorkorderRequest ||
          currentRequestActivity !== request.service.code ||
          currentRequestGroupId !== request.responsible.groupId
        );
      }

      case UpdateInBatchEnum.closeWorkorder: {
        const isServiceRequest =
          request.source === RequestSourceTypeEnum.serviceRequest;
        if (currentRequestActivity === "" && !currentRequestGroupId) {
          return isServiceRequest;
        }
        return (
          isServiceRequest ||
          currentRequestActivity !== request.service.code ||
          currentRequestGroupId !== request.responsible.groupId
        );
      }

      default:
        return false;
    }
  }
}
